import styled from 'styled-components';
import { tokens } from 'design-tokens/warwick/tokens';
import { flexColumn, flexCenter, flexCenterVertical } from 'design-tokens/warwick/mixins';

// 437px / 1920px = 22.76% — matches the Figma 1920×1080 spec
const COLUMN_WIDTH_PERCENT = '22.76%';
const COLUMN_MIN_WIDTH = '437px';

// Outer row: left column + right column + icon area
// flex: 1 so it fills the remaining space inside ContentContainer
export const SettingsPanelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex: 1;
  padding: ${tokens.primitive.space[24]};
  
  /* Child spacing — explicit LTR/RTL fallback for Tizen compatibility */
  & > * + * {
    margin-left: ${tokens.primitive.space[23]};
  }

  [dir="rtl"] & > * + * {
    margin-left: 0;
    margin-right: ${tokens.primitive.space[23]};
  }
`;

/**
 * Shared column wrapper.
 *
 * Props:
 *   sectionFocused {boolean} — when true, adds a gold focus ring around the whole column
 */
export const SettingsColumn = styled.div`
  ${flexColumn}
  width: ${COLUMN_WIDTH_PERCENT};
  min-width: ${COLUMN_MIN_WIDTH};
  flex-shrink: 0;
  background: ${tokens.primitive.color.whiteAlpha75};
  border-radius: ${tokens.primitive.radius[21]};
  padding: ${tokens.primitive.space[24]};
  box-shadow: ${(props) =>
    props.sectionFocused
      ? `${tokens.primitive.shadow.lg}, ${tokens.primitive.shadow.focusRing}`
      : tokens.primitive.shadow.lg};

  > * + * {
    margin-top: ${tokens.primitive.space[12]};
  }
`;

/**
 * Individual list item (used in both columns).
 *
 * Props:
 *   active        {boolean} — cursor is on this item → gold background
 *   selected      {boolean} — saved value, cursor elsewhere → purple bg + gold border
 *   sectionFocused {boolean} — parent section is active → shows focus ring on active item
 */
export const SettingsItem = styled.div`
  ${flexCenterVertical}
  padding: ${tokens.primitive.space[14]};
  border-radius: ${tokens.primitive.radius[14]};
  cursor: pointer;
  background: ${(props) =>
    props.active
      ? tokens.semantic.color.background.rowActive
      : tokens.semantic.color.background.rowDefault};
  border: 2px solid ${(props) =>
    !props.active && props.selected
      ? tokens.primitive.color.gold
      : 'transparent'};
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[22]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.normal};
  color: ${tokens.semantic.color.text.onDark};
  box-shadow: ${(props) =>
    props.active && props.sectionFocused ? tokens.primitive.shadow.focusRing : 'none'};
  white-space: nowrap;
`;

// Icon area — stretches to full row height so the image can be centered
export const IconArea = styled.div`
  flex: 1;
  align-self: stretch;
  ${flexCenter}
`;

/**
 * Inline language illustration (replaces language-outline.svg as <img>).
 * Optional transient props: $panelFill, $glyphStroke — default to Warwick purple / white.
 */
export const LanguageIcon = styled.svg.attrs({
  viewBox: '0 0 310 310',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true,
})`
  width: ${tokens.primitive.space[310]};
  height: ${tokens.primitive.space[310]};
  flex-shrink: 0;

  .language-icon__panel {
    fill: ${(p) => p.$panelFill ?? tokens.primitive.color.purple};
  }

  .language-icon__glyph {
    fill: none;
    stroke: ${(p) => p.$glyphStroke ?? tokens.primitive.color.white};
    stroke-width: 17.2222;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const SourceIcon = styled.svg.attrs({
  viewBox: '0 0 310 310',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true,
})`
  width: ${tokens.primitive.space[310]};
  height: ${tokens.primitive.space[310]};
  flex-shrink: 0;

  .source-icon__panel {
    fill: ${(p) => p.$panelFill ?? tokens.primitive.color.purple};
  }

  .source-icon__glyph {
    fill: none;
    stroke: ${(p) => p.$glyphStroke ?? tokens.primitive.color.white};
    stroke-width: 17.2222;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .source-icon__play {
    fill: ${(p) => p.$glyphStroke ?? tokens.primitive.color.white};
  }
`;
